import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { BillingTransaction } from "@/types/billing-transactions";
import { useBillingTransactions } from "@/hooks/useBillingTransactions";
import { TransactionBalanceSummary } from "./TransactionBalanceSummary";
import { User2, ChevronRight } from "lucide-react";
import { getPaymentUi } from "../utils/getPaymentUi";
import { getStringMeta } from "../utils/getStringMeta";
import { formatDateTime } from "../utils/formatDateTime";

export const TransactionHistoryByEntity = ({
  bookingId,
  guestId,
  title = "Transaction history",
  limit = 100,
}: {
  guestId?: string;
  bookingId?: string;
  title?: string;
  limit?: number;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const filters = useMemo(
    () => ({
      bookingId,
      guestId,
      page: 1,
      limit,
    }),
    [bookingId, guestId, limit],
  );

  const query = useBillingTransactions(1, limit, filters);

  const data = query.data;
  const transactions = data?.transactions ?? [];

  if (query.isLoading) return null;
  if (query.isError) return null;

  return (
    <>
      {transactions?.length > 0 && (
        <TransactionBalanceSummary
          transactions={transactions}
          title={t("billing.transactionSummary")}
        />
      )}

      <div className="mb-4 flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[28px] leading-[32px] font-[400] text-[#0F0F0F]">
            {title}
          </h3>
          <Button
            variant={"ghost"}
            size={"sm"}
            onClick={() => navigate("/billing-transactions")}
          >
            <div className="flex items-center gap-1 text-sm">
              <div className="text-md">All</div>
              <ChevronRight />
            </div>
          </Button>
        </div>

        <div className="mb-3 grid grid-cols-[100px_1fr_160px_180px_220px] gap-6 px-2 py-0 text-sm text-[#6B7280]">
          <div className="text-center">{t("billing.payment")}</div>
          <div className="text-center">{t("billing.comments")}</div>
          <div className="text-center">{t("billing.price")}</div>
          <div className="text-center">{t("billing.time")}</div>
          <div className="text-center">{t("billing.createdBy")}</div>
        </div>

        <div className="divide-y divide-[#E5E7EB] px-2">
          {transactions?.length ? (
            transactions.map((t) => <TransactionRow key={t.id} tx={t} />)
          ) : (
            <p className="py-6 text-center text-sm text-gray-500">
              {t("billing.noTransactionsFound", "No transactions found.")}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

const TransactionRow = ({ tx }: { tx: BillingTransaction }) => {
  const { t } = useTranslation();
  const paymentUi = getPaymentUi(tx, {
    successStatuses: {
      success: t("billing.paymentSuccess"),
      successOffline: t("billing.paymentSuccessOffline"),
      refund: t("billing.paymentRefund"),
      partialRefund: t("billing.partialRefund"),
    },
    failureStatuses: {
      notPaid: t("billing.paymentFailed"),
      notRefunded: t("billing.paymentNotRefund"),
      notCompleted: t("billing.paymentNotCompleted"),
    },
  });
  const createdByName = getStringMeta(tx.metadata, "createdByName");
  const createdByRole = getStringMeta(tx.metadata, "createdByRole");

  const description =
    tx.billingItems?.map((item) => item.description).join(", ") || "";

  return (
    <div className="grid grid-cols-[100px_1fr_160px_180px_220px] items-center gap-6 py-3">
      <div className="flex flex-col items-center gap-1">
        {paymentUi.icon}
        <div className="flex flex-col">
          <div className="text-sm text-[#6B7280]">{paymentUi.title}</div>
        </div>
      </div>

      <div className="text-md text-center leading-[16px] text-[#0F0F0F]">
        {description || "—"}
      </div>

      <div className="text-center text-[16px] leading-[16px] text-[#0F0F0F]">
        {formatMoney(tx.amount, tx.currency)}
      </div>

      <div className="text-center">
        <div className="text-md text-[#0F0F0F]">
          {formatDateTime(tx.createdAt, "dd.MM.yyyy")}
        </div>
        <div className="text-sm text-[#6B7280]">
          {formatDateTime(tx.createdAt, "HH:mm")}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-1">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#93B4D6]">
          <User2 className="h-5 w-5 text-white" />
        </div>
        <div className="text-sm text-[#0F0F0F]">{createdByName || "—"}</div>
        <div className="text-xs text-[#6B7280]">{createdByRole || ""}</div>
      </div>
    </div>
  );
};

function formatMoney(amountMinor: number, currency: string) {
  const value = amountMinor / 100;
  return `${trimZeros(value)} ${currency}`;
}

function trimZeros(n: number) {
  const s = n.toFixed(2);
  return s.endsWith(".00") ? String(Math.trunc(n)) : s;
}
